import { useState, useEffect } from "react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertCircle,
  Loader2,
  User,
  Phone,
  Mail,
  CreditCard,
  Car,
  Calendar,
  Users,
  Upload,
  Plus,
  X,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function CheckInModal({ room, categories, onClose, onSuccess }) {
  const [step, setStep] = useState(1); // 1: Guest Info, 2: Booking Details, 3: Payment
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tariffs, setTariffs] = useState([]);
  const [idProofFile, setIdProofFile] = useState(null);
  const [additionalGuests, setAdditionalGuests] = useState([]);

  const [guestData, setGuestData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    id_proof_type: "Aadhar",
    id_proof_number: "",
    gender: "Male",
    company_name: "",
    gst_number: "",
  });

  const [bookingData, setBookingData] = useState({
    check_in_date: new Date().toISOString().split("T")[0],
    check_out_date: new Date(Date.now() + 86400000).toISOString().split("T")[0], // Tomorrow
    adults: 1,
    children: 0,
    plan_type: "EP",
    tariff_plan_id: null,
    room_rent_per_night: 0,
    extra_bed_count: 0,
    vehicle_number: "",
    special_requests: "",
  });

  const [paymentData, setPaymentData] = useState({
    advance_paid: 0,
    advance_payment_mode: "Cash",
  });

  // Fetch tariffs for the room category
  useEffect(() => {
    const fetchTariffs = async () => {
      try {
        const response = await api.get(`/hotel/tariffs/${room.category_id}`);
        setTariffs(response.data.tariffs || []);

        // Set default tariff
        if (response.data.tariffs.length > 0) {
          const defaultTariff =
            response.data.tariffs.find((t) => t.plan_type === "EP") ||
            response.data.tariffs[0];
          setBookingData((prev) => ({
            ...prev,
            tariff_plan_id: defaultTariff.id,
            room_rent_per_night: defaultTariff.weekday_price,
            plan_type: defaultTariff.plan_type,
          }));
        }
      } catch (error) {
        console.error("Failed to fetch tariffs:", error);
      }
    };

    if (room?.category_id) {
      fetchTariffs();
    }
  }, [room]);

  // Search and auto-fill guest by phone
  const handlePhoneBlur = async () => {
    if (guestData.phone.length >= 10) {
      try {
        const response = await api.get(
          `/hotel/guests/search?q=${guestData.phone}`,
        );
        if (response.data.guests && response.data.guests.length > 0) {
          const guest = response.data.guests[0];
          setGuestData({
            ...guest,
            phone: guestData.phone, // Keep the entered phone
          });
        }
      } catch (error) {
        console.error("Guest search failed:", error);
      }
    }
  };

  // Calculate total nights
  const calculateNights = () => {
    const checkIn = new Date(bookingData.check_in_date);
    const checkOut = new Date(bookingData.check_out_date);
    const diff = checkOut - checkIn;
    return Math.max(1, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  // Calculate GST rate automatically based on Indian GST rules for hotels
  const calculateGSTRate = (roomRent) => {
    if (roomRent >= 7500) {
      return 18.0; // Luxury category
    } else if (roomRent > 1000) {
      return 12.0; // Standard category
    } else {
      return 0.0; // Budget category (exempt)
    }
  };

  // Calculate total amount
  const calculateTotal = () => {
    const nights = calculateNights();
    const roomTotal = bookingData.room_rent_per_night * nights;
    const gstRate = calculateGSTRate(roomTotal);
    const gstAmount = (roomTotal * gstRate) / 100;
    return {
      nights,
      roomTotal,
      gstRate,
      gstAmount,
      grandTotal: roomTotal + gstAmount,
    };
  };

  const handleTariffChange = (tariffId) => {
    const tariff = tariffs.find((t) => t.id === parseInt(tariffId));
    if (tariff) {
      setBookingData((prev) => ({
        ...prev,
        tariff_plan_id: tariff.id,
        plan_type: tariff.plan_type,
        room_rent_per_night: tariff.weekday_price,
      }));
    }
  };

  const handleSubmit = async () => {
    setError("");
    setLoading(true);

    try {
      // Step 1: Save/Find Guest
      const guestResponse = await api.post("/hotel/guests", guestData);
      const guestId = guestResponse.data.guest.id;

      // Step 1.5: Upload ID proof if provided
      if (idProofFile) {
        const formData = new FormData();
        formData.append("id_proof", idProofFile);
        try {
          await api.post(`/hotel/guests/${guestId}/id-proof`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
        } catch (uploadError) {
          console.error("ID proof upload failed:", uploadError);
          // Continue with check-in even if upload fails
        }
      }

      // Step 2: Create Booking & Check-In
      const checkInPayload = {
        room_id: room.id,
        guest_id: guestId,
        ...bookingData,
        ...paymentData,
        additional_guests: JSON.stringify(additionalGuests),
        staff_name: "Reception", // TODO: Get from logged-in user
      };

      await api.post("/hotel/bookings/checkin", checkInPayload);

      onSuccess();
    } catch (err) {
      console.error("Check-in error:", err);
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || "Check-in failed. Please try again.";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const addAdditionalGuest = () => {
    setAdditionalGuests([...additionalGuests, { name: "", age: "", gender: "Male" }]);
  };

  const removeAdditionalGuest = (index) => {
    setAdditionalGuests(additionalGuests.filter((_, i) => i !== index));
  };

  const updateAdditionalGuest = (index, field, value) => {
    const updated = [...additionalGuests];
    updated[index][field] = value;
    setAdditionalGuests(updated);
  };

  const totals = calculateTotal();

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading font-bold text-[#2D2C2A]">
            Check-In - Room {room.room_number}
          </DialogTitle>
          <div className="text-sm text-[#7A7873]">
            {room.category_name} • {room.floor}
          </div>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Step Indicator */}
        <div className="flex items-center justify-between mb-6">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center flex-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  step >= s
                    ? "bg-[#D4AF37] text-white"
                    : "bg-gray-200 text-gray-500"
                }`}
              >
                {s}
              </div>
              {s < 3 && (
                <div
                  className={`flex-1 h-1 mx-2 ${step > s ? "bg-[#D4AF37]" : "bg-gray-200"}`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Guest Information */}
        {step === 1 && (
          <div className="space-y-4">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <User size={20} className="text-[#D4AF37]" />
              Guest Information
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Guest Name *</Label>
                <Input
                  id="name"
                  value={guestData.name}
                  onChange={(e) =>
                    setGuestData({ ...guestData, name: e.target.value })
                  }
                  placeholder="Full Name"
                  required
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone *</Label>
                <div className="flex gap-2">
                  <Phone size={16} className="mt-3 text-gray-400" />
                  <Input
                    id="phone"
                    value={guestData.phone}
                    onChange={(e) =>
                      setGuestData({ ...guestData, phone: e.target.value })
                    }
                    onBlur={handlePhoneBlur}
                    placeholder="Mobile Number"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <div className="flex gap-2">
                  <Mail size={16} className="mt-3 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    value={guestData.email}
                    onChange={(e) =>
                      setGuestData({ ...guestData, email: e.target.value })
                    }
                    placeholder="Email Address"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="id_proof_type">ID Proof Type</Label>
                <Select
                  value={guestData.id_proof_type}
                  onValueChange={(val) =>
                    setGuestData({ ...guestData, id_proof_type: val })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Aadhar">Aadhar Card</SelectItem>
                    <SelectItem value="Passport">Passport</SelectItem>
                    <SelectItem value="Driving License">
                      Driving License
                    </SelectItem>
                    <SelectItem value="Voter ID">Voter ID</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="id_proof_number">ID Proof Number</Label>
                <Input
                  id="id_proof_number"
                  value={guestData.id_proof_number}
                  onChange={(e) =>
                    setGuestData({
                      ...guestData,
                      id_proof_number: e.target.value,
                    })
                  }
                  placeholder="ID Number"
                />
              </div>

              <div>
                <Label htmlFor="gender">Gender</Label>
                <Select
                  value={guestData.gender}
                  onValueChange={(val) =>
                    setGuestData({ ...guestData, gender: val })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="company_name">Company Name (Optional)</Label>
                <Input
                  id="company_name"
                  value={guestData.company_name}
                  onChange={(e) =>
                    setGuestData({ ...guestData, company_name: e.target.value })
                  }
                  placeholder="For corporate bookings"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="address">Address</Label>
                <Textarea
                  id="address"
                  value={guestData.address}
                  onChange={(e) =>
                    setGuestData({ ...guestData, address: e.target.value })
                  }
                  placeholder="Full Address"
                  rows={2}
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="id_proof_file">Upload ID Proof (Optional)</Label>
                <div className="flex items-center gap-2">
                  <Upload size={16} className="text-gray-400" />
                  <Input
                    id="id_proof_file"
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setIdProofFile(e.target.files[0])}
                    className="cursor-pointer"
                  />
                </div>
                {idProofFile && (
                  <div className="text-xs text-green-600 mt-1">
                    ✓ {idProofFile.name} selected
                  </div>
                )}
                <div className="text-xs text-[#7A7873] mt-1">
                  Supported: JPG, PNG, PDF (Max 5MB)
                </div>
              </div>

              <div className="col-span-2 border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label>Additional Guests (Family Members)</Label>
                  <Button
                    type="button"
                    onClick={addAdditionalGuest}
                    variant="outline"
                    size="sm"
                    className="gap-1"
                  >
                    <Plus size={14} />
                    Add Guest
                  </Button>
                </div>
                {additionalGuests.map((guest, index) => (
                  <div key={index} className="grid grid-cols-4 gap-2 mb-2 items-end">
                    <Input
                      placeholder="Name"
                      value={guest.name}
                      onChange={(e) => updateAdditionalGuest(index, "name", e.target.value)}
                    />
                    <Input
                      placeholder="Age"
                      type="number"
                      value={guest.age}
                      onChange={(e) => updateAdditionalGuest(index, "age", e.target.value)}
                    />
                    <Select
                      value={guest.gender}
                      onValueChange={(val) => updateAdditionalGuest(index, "gender", val)}
                    >
                      <SelectTrigger className="h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      onClick={() => removeAdditionalGuest(index)}
                      variant="destructive"
                      size="sm"
                    >
                      <X size={14} />
                    </Button>
                  </div>
                ))}
                {additionalGuests.length === 0 && (
                  <div className="text-xs text-[#7A7873] text-center py-2">
                    No additional guests added
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={() => setStep(2)}
                disabled={!guestData.name || !guestData.phone}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Booking Details */}
        {step === 2 && (
          <div className="space-y-4">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Calendar size={20} className="text-[#D4AF37]" />
              Booking Details
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="check_in_date">Check-In Date *</Label>
                <Input
                  id="check_in_date"
                  type="date"
                  value={bookingData.check_in_date}
                  onChange={(e) =>
                    setBookingData({
                      ...bookingData,
                      check_in_date: e.target.value,
                    })
                  }
                  required
                />
              </div>

              <div>
                <Label htmlFor="check_out_date">Check-Out Date *</Label>
                <Input
                  id="check_out_date"
                  type="date"
                  value={bookingData.check_out_date}
                  onChange={(e) =>
                    setBookingData({
                      ...bookingData,
                      check_out_date: e.target.value,
                    })
                  }
                  min={bookingData.check_in_date}
                  required
                />
              </div>

              <div>
                <Label htmlFor="adults">Adults *</Label>
                <div className="flex gap-2">
                  <Users size={16} className="mt-3 text-gray-400" />
                  <Input
                    id="adults"
                    type="number"
                    min="1"
                    value={bookingData.adults}
                    onChange={(e) =>
                      setBookingData({
                        ...bookingData,
                        adults: parseInt(e.target.value),
                      })
                    }
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="children">Children</Label>
                <Input
                  id="children"
                  type="number"
                  min="0"
                  value={bookingData.children}
                  onChange={(e) =>
                    setBookingData({
                      ...bookingData,
                      children: parseInt(e.target.value),
                    })
                  }
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="tariff">Room Plan & Tariff *</Label>
                <Select
                  value={bookingData.tariff_plan_id?.toString()}
                  onValueChange={handleTariffChange}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tariffs.map((tariff) => (
                      <SelectItem key={tariff.id} value={tariff.id.toString()}>
                        {tariff.plan_type} - ₹{tariff.weekday_price}/night (
                        {tariff.season})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-xs text-[#7A7873] mt-1">
                  EP: Room Only | CP: Room + Breakfast | MAP: Room + 2 Meals |
                  AP: All Meals
                </div>
              </div>

              <div>
                <Label htmlFor="extra_bed_count">Extra Bed</Label>
                <Input
                  id="extra_bed_count"
                  type="number"
                  min="0"
                  value={bookingData.extra_bed_count}
                  onChange={(e) =>
                    setBookingData({
                      ...bookingData,
                      extra_bed_count: parseInt(e.target.value),
                    })
                  }
                />
              </div>

              <div>
                <Label htmlFor="vehicle_number">Vehicle Number</Label>
                <div className="flex gap-2">
                  <Car size={16} className="mt-3 text-gray-400" />
                  <Input
                    id="vehicle_number"
                    value={bookingData.vehicle_number}
                    onChange={(e) =>
                      setBookingData({
                        ...bookingData,
                        vehicle_number: e.target.value,
                      })
                    }
                    placeholder="DL01AB1234"
                  />
                </div>
              </div>

              <div className="col-span-2">
                <Label htmlFor="special_requests">Special Requests</Label>
                <Textarea
                  id="special_requests"
                  value={bookingData.special_requests}
                  onChange={(e) =>
                    setBookingData({
                      ...bookingData,
                      special_requests: e.target.value,
                    })
                  }
                  placeholder="Any special requirements..."
                  rows={2}
                />
              </div>
            </div>

            {/* Billing Summary */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span>
                  Room Rent ({totals.nights}{" "}
                  {totals.nights === 1 ? "night" : "nights"})
                </span>
                <span className="font-semibold">
                  ₹{totals.roomTotal.toFixed(2)}
                </span>
              </div>
              
              {/* GST Auto-Calculation Info */}
              <div className="py-2 border-t bg-blue-50 -mx-4 px-4">
                <div className="flex items-start gap-2 text-xs text-blue-800">
                  <span className="font-semibold">ℹ️ GST Auto-Applied:</span>
                  <div>
                    {totals.gstRate === 18 && (
                      <span>18% (Room rent ≥ ₹7,500) • Luxury category</span>
                    )}
                    {totals.gstRate === 12 && (
                      <span>12% (Room rent ₹1,001-₹7,499) • Standard category</span>
                    )}
                    {totals.gstRate === 0 && (
                      <span>0% (Room rent ≤ ₹1,000) • Budget category</span>
                    )}
                  </div>
                </div>
              </div>

              {totals.gstRate > 0 && (
                <div className="flex justify-between text-sm">
                  <span>GST @ {totals.gstRate}%</span>
                  <span className="font-semibold">₹{totals.gstAmount.toFixed(2)}</span>
                </div>
              )}
              
              <div className="border-t pt-2 flex justify-between font-bold">
                <span>Total Amount</span>
                <span className="text-[#D4AF37]">
                  ₹{totals.grandTotal.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="flex justify-between gap-2 pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button onClick={() => setStep(3)}>Next</Button>
            </div>
          </div>
        )}

        {/* Step 3: Payment */}
        {step === 3 && (
          <div className="space-y-4">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <CreditCard size={20} className="text-[#D4AF37]" />
              Advance Payment
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="advance_paid">Advance Amount</Label>
                <Input
                  id="advance_paid"
                  type="number"
                  min="0"
                  step="0.01"
                  value={paymentData.advance_paid}
                  onChange={(e) =>
                    setPaymentData({
                      ...paymentData,
                      advance_paid: parseFloat(e.target.value) || 0,
                    })
                  }
                  placeholder="0.00"
                />
              </div>

              <div>
                <Label htmlFor="payment_mode">Payment Mode</Label>
                <Select
                  value={paymentData.advance_payment_mode}
                  onValueChange={(val) =>
                    setPaymentData({
                      ...paymentData,
                      advance_payment_mode: val,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Card">Card</SelectItem>
                    <SelectItem value="UPI">UPI</SelectItem>
                    <SelectItem value="Online">Online Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Final Summary */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <h4 className="font-semibold mb-3">Booking Summary</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-[#7A7873]">Guest:</span>
                <span className="font-semibold">{guestData.name}</span>

                <span className="text-[#7A7873]">Room:</span>
                <span className="font-semibold">
                  {room.room_number} ({room.category_name})
                </span>

                <span className="text-[#7A7873]">Check-In:</span>
                <span className="font-semibold">
                  {bookingData.check_in_date}
                </span>

                <span className="text-[#7A7873]">Check-Out:</span>
                <span className="font-semibold">
                  {bookingData.check_out_date}
                </span>

                <span className="text-[#7A7873]">Plan Type:</span>
                <span className="font-semibold">{bookingData.plan_type}</span>

                <span className="text-[#7A7873]">Guests:</span>
                <span className="font-semibold">
                  {bookingData.adults} Adults, {bookingData.children} Children
                </span>
              </div>

              <div className="border-t pt-3 mt-3 space-y-1">
                <div className="flex justify-between">
                  <span>Total Bill:</span>
                  <span className="font-semibold">
                    ₹{totals.grandTotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>Advance Paid:</span>
                  <span className="font-semibold">
                    ₹{paymentData.advance_paid.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Balance Due:</span>
                  <span className="text-[#D4AF37]">
                    ₹{(totals.grandTotal - paymentData.advance_paid).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-between gap-2 pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-[#D4AF37] hover:bg-[#C5A028]"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Confirm Check-In"
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
